import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '../../ui/catalyst/button';
import { Checkbox } from '../../ui/catalyst/checkbox';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from '../../ui/catalyst/dialog';
import { Field, FieldGroup, Fieldset, Label } from '../../ui/catalyst/fieldset';
import { Input } from '../../ui/catalyst/input';
import { Textarea } from '../../ui/catalyst/textarea';

interface CreateThreadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateThread: (title: string, initialTask: string | null) => Promise<void>;
}

const defaultInitialTask = `
My goal is to create comprehensive facts, liability and medical record summaries for a demand letter.

First, I need to review all available files to identify relevant documents. Then, I will:

1. Create a detailed facts and liability summary by:
   - Reviewing police reports, witness statements, and any investigation documents
   - Analyzing photos, diagrams, or other evidence of the incident
   - Identifying clear liability factors and any contributory negligence
   - Establishing a clear timeline of events

2. Generate a thorough medical record summary by:
   - Reviewing all medical records chronologically
   - Documenting initial emergency treatment and diagnoses
   - Tracking all follow-up care, treatments, and procedures
   - Noting physician opinions on causation and prognosis
   - Identifying any pre-existing conditions and their relation
   - Summarizing permanent injuries or limitations
   - Compiling all medical expenses and billing records

Please ensure all summaries include specific citations to source documents to support each key point.
`;

export const CreateThreadDialog = ({
  isOpen,
  onClose,
  onCreateThread,
}: CreateThreadDialogProps) => {
  const [title, setTitle] = useState('');
  const [includeInitialTask, setIncludeInitialTask] = useState(false);
  const [initialTask, setInitialTask] = useState(defaultInitialTask);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || isLoading) return;

    setIsLoading(true);
    try {
      await onCreateThread(
        title,
        includeInitialTask ? initialTask.trim() : null,
      );
      setTitle('');
      setIncludeInitialTask(false);
      setInitialTask(defaultInitialTask);
    } catch (error) {
      toast.error('Failed to create thread. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setTitle('');
      setIncludeInitialTask(false);
      setInitialTask(defaultInitialTask);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose}>
      <DialogTitle>Create New Thread</DialogTitle>
      <DialogBody>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label htmlFor="thread-title">Thread Title</Label>
                <Input
                  id="thread-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter a title for your new thread"
                  autoFocus
                  disabled={isLoading}
                />
              </Field>
              <Field className="mt-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="include-initial-task"
                    checked={includeInitialTask}
                    onChange={(value: boolean) => setIncludeInitialTask(value)}
                    disabled={isLoading}
                  />
                  <Label htmlFor="include-initial-task">
                    Include initial task
                  </Label>
                </div>
              </Field>
              {includeInitialTask && (
                <Field className="mt-4">
                  <Label htmlFor="initial-task">Initial Task</Label>
                  <Textarea
                    id="initial-task"
                    value={initialTask}
                    onChange={(e) => setInitialTask(e.target.value)}
                    placeholder="Describe the initial task"
                    rows={4}
                    disabled={isLoading}
                  />
                </Field>
              )}
            </FieldGroup>
          </Fieldset>
        </form>
      </DialogBody>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          color="blue"
          onClick={handleSubmit}
          disabled={
            !title.trim() ||
            (includeInitialTask && !initialTask.trim()) ||
            isLoading
          }
        >
          {isLoading ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
